import React, { useState } from 'react';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Info, Download, CheckCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useTranslation } from '../../TranslationContext';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A4DE6C', '#D0ED57', '#8884D8'];

interface ChartData {
  name: string;
  value: number;
  id: number;
}

interface UsersDistributionChartProps {
  chartData: ChartData[];
  isLoading: boolean;
}

const UsersDistributionChart: React.FC<UsersDistributionChartProps> = ({ 
  chartData, 
  isLoading
}) => {
  const { t } = useTranslation();
  const [downloadStatus, setDownloadStatus] = useState<{type: string, visible: boolean}>({type: '', visible: false});
  const totalUsers = chartData.reduce((sum, item) => sum + item.value, 0);

  const showDownloadFeedback = (type: string) => {
    setDownloadStatus({type, visible: true});
    setTimeout(() => setDownloadStatus({type: '', visible: false}), 2000);
  };

  const handleExportCSV = () => {
    try {
      if (totalUsers === 0) return;
      const headers = [t('application'), t('users'), t('percentage')];
      const data = chartData.map(item => [
        item.name,
        item.value,
        `${((item.value / totalUsers) * 100).toFixed(2)}%`
      ]);

      const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('users_distribution'));
      XLSX.writeFile(wb, t('users_distribution_filename_csv'));
      
      showDownloadFeedback('csv');
    } catch (error) {
      console.error(t('error_export_csv_log'), error);
    }
  };
 
  const handleExportPDF = () => {
    try {
      if (totalUsers === 0) return;

      const doc = new jsPDF();
      doc.text(t('users_distribution'), 14, 15);

      const headers = [t('application'), t('users'), t('percentage')];
      const data = chartData.map(item => [
        item.name,
        item.value.toString(),
        `${((item.value / totalUsers) * 100).toFixed(2)}%`
      ]);

      (doc as any).autoTable({
        head: [headers],
        body: data,
        startY: 20,
        theme: 'grid',
        headStyles: { 
          fillColor: [30, 41, 59], // slate-800
          textColor: 255 
        },
      });

      doc.save(t('users_distribution_filename_pdf'));
      showDownloadFeedback('pdf');
    } catch (error) {
      console.error(t('error_export_pdf_log'), error);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 md:p-6">
      {/* Notification de téléchargement */}
      {downloadStatus.visible && (
        <div className="fixed bottom-4 right-4 bg-slate-700 text-white px-4 py-2 rounded-md flex items-center animate-fade-in">
          <CheckCircle className="h-5 w-5 mr-2" />
          {downloadStatus.type === 'csv' ? t('csv_downloaded') : t('pdf_downloaded')}
        </div>
      )}

      <div className="flex justify-between mb-3">
        <div className="flex items-center">
          <h5 className="text-lg font-medium text-slate-700">
            {t('users_distribution')}
          </h5>
          <button
            data-tooltip-target="chart-info-tooltip"
            type="button"
            className="text-slate-500 hover:text-slate-700 ml-2"
            aria-label={t('chart_info')}
          >
            <Info className="w-4 h-4" />
          </button>
        </div>
      </div>

      {chartData.length > 0 && totalUsers > 0 ? (
        <div className="h-64 md:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                labelLine={false}
              >
                {chartData.map((_entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]} 
                    stroke="#fff"
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number, name: string, props: any) => [
                  // t('users_count', { count: value }), 
                  t('users_count', { count: value }), 
                  `${name} (${(props.payload.percent * 100).toFixed(2)}%)`
                ]}
                contentStyle={{
                  backgroundColor: 'white',
                  borderColor: '#e2e8f0',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  color: '#334155'
                }}
              />
              <Legend 
                formatter={(_value, _entry, index) => chartData[index]?.name || t('app_fallback', { id: chartData[index]?.id })}
                wrapperStyle={{ color: '#64748b' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 md:h-80 flex items-center justify-center text-slate-500">
          {isLoading ? t('loading_user_data') : t('no_user_data')}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-slate-200">
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-500">
            {t('showing_applications', { appCount: chartData.length, userCount: totalUsers })}
          </span>
          <div className="flex space-x-2">
            <button
              onClick={handleExportCSV}
              disabled={totalUsers === 0}
              className={`flex items-center text-sm font-medium rounded-lg px-3 py-2 ${
                totalUsers === 0 
                  ? 'text-slate-400 cursor-not-allowed' 
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
              aria-label={t('export_csv')}
            >
              <Download className="w-4 h-4 mr-1" />
              CSV
            </button>
            <button
              onClick={handleExportPDF}
              disabled={totalUsers === 0}
              className={`flex items-center text-sm font-medium rounded-lg px-3 py-2 ${
                totalUsers === 0 
                  ? 'text-slate-400 cursor-not-allowed' 
                  : 'text-slate-700 hover:bg-slate-100'
              }`}
              aria-label={t('export_pdf')}
            >
              <Download className="w-4 h-4 mr-1" />
              PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersDistributionChart;

// import React, { useState } from 'react';
// import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
// import { Info, Download, CheckCircle } from 'lucide-react';
// import * as XLSX from 'xlsx';
// import { jsPDF } from 'jspdf';
// import 'jspdf-autotable';

// const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A4DE6C', '#D0ED57', '#8884D8'];

// interface ChartData {
//   name: string;
//   value: number;
//   id: number;
// }

// interface UsersDistributionChartProps {
//   chartData: ChartData[];
//   isLoading: boolean;
// }

// const UsersDistributionChart: React.FC<UsersDistributionChartProps> = ({ 
//   chartData, 
//   isLoading
// }) => {
//   const [downloadStatus, setDownloadStatus] = useState<{type: string, visible: boolean}>({type: '', visible: false});
//   const totalUsers = chartData.reduce((sum, item) => sum + item.value, 0);

//   const showDownloadFeedback = (type: string) => {
//     setDownloadStatus({type, visible: true});
//     setTimeout(() => setDownloadStatus({type: '', visible: false}), 2000);
//   };

//   const handleExportCSV = () => {
    
//     try {
//       if (totalUsers === 0) return;
//       console.log('chartData',chartData)
//       const headers = ['Application', 'Users', 'Percentage'];
//       const data = chartData.map(item => [
//         item.name,
//         item.value,
//         `${((item.value / totalUsers) * 100).toFixed(2)}%`
//       ]);

//       const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
//       const wb = XLSX.utils.book_new();
//       XLSX.utils.book_append_sheet(wb, ws, 'UsersDistribution');
//       XLSX.writeFile(wb, 'users_distribution.csv');
      
//       showDownloadFeedback('csv');
//     } catch (error) {
//       console.error("Export CSV error:", error);
//     }
//   };
 
//   const handleExportPDF = () => {
//     try {
//       if (totalUsers === 0) return;

//       const doc = new jsPDF();
//       doc.text('Users Distribution by Application', 14, 15);

//       const headers = ['Application', 'Users', 'Percentage'];
//       const data = chartData.map(item => [
//         item.name,
//         item.value.toString(),
//         `${((item.value / totalUsers) * 100).toFixed(2)}%`
//       ]);

//       (doc as any).autoTable({
//         head: [headers],
//         body: data,
//         startY: 20,
//         theme: 'grid',
//         headStyles: { 
//           fillColor: [30, 41, 59], // slate-800
//           textColor: 255 
//         },
//       });

//       doc.save('users_distribution.pdf');
//       showDownloadFeedback('pdf');
//     } catch (error) {
//       console.error("Export PDF error:", error);
//     }
    
//   };

//   return (
//     <div className="bg-white shadow rounded-lg p-4 md:p-6">
//       {/* Notification de téléchargement */}
//       {downloadStatus.visible && (
//         <div className="fixed bottom-4 right-4 bg-slate-700 text-white px-4 py-2 rounded-md flex items-center animate-fade-in">
//           <CheckCircle className="h-5 w-5 mr-2" />
//           {downloadStatus.type === 'csv' ? 'CSV downloaded!' : 'PDF downloaded!'}
//         </div>
//       )}

//       <div className="flex justify-between mb-3">
//         <div className="flex items-center">
//           <h5 className="text-lg font-medium text-slate-700">
//             Users Distribution by Application
//           </h5>
//           <button
//             data-tooltip-target="chart-info-tooltip"
//             type="button"
//             className="text-slate-500 hover:text-slate-700 ml-2"
//           >
//             <Info className="w-4 h-4" />
//           </button>
//         </div>
//       </div>

//       {chartData.length > 0 && totalUsers > 0 ? (
//         <div className="h-64 md:h-80">
//           <ResponsiveContainer width="100%" height="100%">
//             <PieChart>
//               <Pie
//                 data={chartData}
//                 cx="50%"
//                 cy="50%"
//                 innerRadius={60}
//                 outerRadius={80}
//                 paddingAngle={5}
//                 dataKey="value"
//                 label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
//                 labelLine={false}
//               >
                
//                    {chartData.map((entry, index) => (
//                   <Cell 
//                     key={`cell-${index}`} 
//                     fill={COLORS[index % COLORS.length]} 
//                     stroke="#fff"
//                     strokeWidth={1}
//                   />
//                 ))}
//               </Pie>
//               <Tooltip 
//                 formatter={(value: number, name: string, props: any) => [
//                   `${value} users`, 
//                   `${name} (${(props.payload.percent * 100).toFixed(2)}%)`
//                 ]}
//                 contentStyle={{
//                   backgroundColor: 'white',
//                   borderColor: '#e2e8f0',
//                   borderRadius: '0.5rem',
//                   boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
//                   color: '#334155'
//                 }}
//               />
//               <Legend 
//                 formatter={(value, entry, index) => chartData[index]?.name || `App ${chartData[index]?.id}`}
//                 wrapperStyle={{ color: '#64748b' }}
//               />
//             </PieChart>
//           </ResponsiveContainer>
//         </div>
//       ) : (
//         <div className="h-64 md:h-80 flex items-center justify-center text-slate-500">
//           {isLoading ? 'Loading user data...' : 'No user data available'}
//         </div>
//       )}

//       <div className="mt-4 pt-4 border-t border-slate-200">
//         <div className="flex justify-between items-center">
//           <span className="text-sm text-slate-500">
//             Showing {chartData.length} applications with {totalUsers} users
//           </span>
//           <div className="flex space-x-2">
//             <button
//               onClick={handleExportCSV}
//               disabled={totalUsers === 0}
//               className={`flex items-center text-sm font-medium rounded-lg px-3 py-2 ${
//                 totalUsers === 0 
//                   ? 'text-slate-400 cursor-not-allowed' 
//                   : 'text-slate-700 hover:bg-slate-100'
//               }`}
//             >
//               <Download className="w-4 h-4 mr-1" />
//               CSV
//             </button>
//             <button
//               onClick={handleExportPDF}
//               disabled={totalUsers === 0}
//               className={`flex items-center text-sm font-medium rounded-lg px-3 py-2 ${
//                 totalUsers === 0 
//                   ? 'text-slate-400 cursor-not-allowed' 
//                   : 'text-slate-700 hover:bg-slate-100'
//               }`}
//             >
//               <Download className="w-4 h-4 mr-1" />
//               PDF
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default UsersDistributionChart;
